import { NormalizedCacheObject } from 'apollo-boost';
import * as React from 'react';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import { applyMiddleware, createStore } from 'redux';
import { createEpicMiddleware } from 'redux-observable';

import {
    fetchPaymentData,
    WalletSwitchOffLimitPopupAction
} from '../../actions/walletSwitchOffLimitEditPopup';
import { DNAApolloClient } from '../../api/DNAApolloClient/DNAApolloClient';
import { WalletDataProvider } from '../../api/wallet/WalletDataProvider';
import { WalletMutationExecutor } from '../../api/wallet/WalletMutationExecutor';
import { WalletSwitchOffLimitEditPopupContainer } from '../../containers/WalletSwitchOffLimitEditPopupContainer/WalletSwitchOffLimitEditPopupContainer';
import { getDNAApolloClient } from '../../declarations/apollo';
import { walletSwitchOffLimitEditPopupWrapperEpics } from '../../epics/walletSwitchOffLimitEditPopup/walletSwitchOffLimitEditPopup';
import { walletSwitchOffLimitPopup } from '../../reducers/walletSwitchOffLimitEditPopup';
import { getCSRFToken } from '../../service/ApiHelpers/ApiHelpers';
import { formatNumber } from '../../service/FormatHelpers/FormatHelpers';
// импорты операторов Rxjs
import '../../service/Rx';
import { UrlHelpers } from '../../service/UrlHelpers';
import { CurrencyCode } from '../../typings/graphql-grid';
import { WalletSwitchOffLimitPopupState } from '../../typings/walletState';
import { Notice } from '../Notice/Notice';

interface OnSetSwitchOffLimitCallbackValue {
    sum: number;
    payerId: string;
    paymentMethodId: string;
}

export interface WalletSwitchOffLimitEditPopupWrapperEpicDeps {
    client: DNAApolloClient<NormalizedCacheObject>;
    walletDataProvider: WalletDataProvider;
    walletMutationExecutor: WalletMutationExecutor;
    actionCallbacks: {
        onClose?: () => void;
        onReset?: (sum: number) => void;
        onSet?: (value: OnSetSwitchOffLimitCallbackValue) => void;
    };
}

interface WalletSwitchOffLimitEditPopupWrapperProps {
    login: string;
    sum: number;
    minSum: number;
    maxSum: number;
    currency: CurrencyCode;
    canResetLimit: boolean;
    isVisible: boolean;
    onClose?: () => void;
    onReset?: () => void;
    onSet?: (value: OnSetSwitchOffLimitCallbackValue) => void;
    isNdsActive?: boolean;
}

export class WalletSwitchOffLimitEditPopupWrapper extends React.PureComponent<
    WalletSwitchOffLimitEditPopupWrapperProps
> {
    private store: Store<WalletSwitchOffLimitPopupState | undefined>;

    constructor(props: WalletSwitchOffLimitEditPopupWrapperProps) {
        super(props);

        const {
            sum,
            isVisible,
            minSum,
            maxSum,
            currency,
            canResetLimit,
            login,
            isNdsActive
        } = this.props;
        const epicDeps = this.getEpicDeps();
        const epicMiddleware = createEpicMiddleware<
            WalletSwitchOffLimitPopupAction,
            WalletSwitchOffLimitPopupAction,
            WalletSwitchOffLimitPopupState,
            WalletSwitchOffLimitEditPopupWrapperEpicDeps
        >({
            dependencies: epicDeps
        });

        this.store = createStore(
            walletSwitchOffLimitPopup,
            {
                tld: UrlHelpers.getTld(),
                isVisible: isVisible,
                isSaving: false,
                isResetting: false,
                value: {
                    sum: sum ? formatNumber(sum, 2) : undefined
                },
                minSum: minSum,
                maxSum: maxSum,
                currency: currency,
                canResetLimit: canResetLimit,
                login: login,
                isNdsActive: isNdsActive
            },
            applyMiddleware(epicMiddleware)
        );
        epicMiddleware.run(walletSwitchOffLimitEditPopupWrapperEpics);
    }

    render() {
        return (
            // https://github.com/apollographql/react-apollo/pull/729
            <>
                <Provider store={this.store}>
                    <WalletSwitchOffLimitEditPopupContainer />
                </Provider>

                <Notice stack={{ limit: 1 }} timeout={'none'} />
            </>
        );
    }

    componentDidMount() {
        this.store.dispatch(fetchPaymentData());
    }

    private getEpicDeps(): WalletSwitchOffLimitEditPopupWrapperEpicDeps {
        const { login, onClose, onReset, onSet } = this.props;

        const dnaClient = getDNAApolloClient(login, getCSRFToken);
        const walletDataProvider = new WalletDataProvider(dnaClient);
        const walletMutationExecutor = new WalletMutationExecutor(dnaClient);

        return {
            client: dnaClient,
            walletDataProvider,
            walletMutationExecutor,
            actionCallbacks: {
                onClose,
                onReset,
                onSet
            }
        };
    }
}
